
import { TrashIcon, PlusIcon } from "@heroicons/vue/24/solid";
import { defineComponent } from "vue";
import DailyReportRepository from "@/repositories/DailyReportRepository";
import logger from "@/services/logger";
import { ReportImageEntry } from "@/types/DailyReport";

export default defineComponent({
  name: "GalleryImageUpload",
  props: ["savedImages", "customImages"],
  emits: ["addCustom"],
  components: {
    TrashIcon,
    PlusIcon,
  },

  data() {
    return {
      images: this.customImages as ReportImageEntry[],
      isDragging: false as boolean,
      allowedExtensions: [".jpg", ".jpeg", ".png", ".heic", ".heif"],
      loading: false as boolean,
    };
  },

  methods: {
    async handleFileUpload(event: Event) {
      const target = event.target as HTMLInputElement;
      const files = target.files as FileList;
      let filesToUpload = Array.from(files);

      const handleImageLoad = (imageUrl: string) => {
        this.saveCustomImage(imageUrl);
      };

      const handleFileRead = () => {
        return (event: ProgressEvent<FileReader>) => {
          const target = event.target as FileReader;
          const imageUrl = target.result as string;
          handleImageLoad(imageUrl);
        };
      };

      const heic2any = (await import("heic2any")).default;

      for (const file of filesToUpload) {
        if (file.type === "image/heif" || file.type === "image/heic") {
          this.loading = true;
          try {
            const convertedBlob = await heic2any({
              blob: file,
              toType: "image/jpeg",
            });
            const reader = new FileReader();
            reader.onload = handleFileRead();
            reader.readAsDataURL(convertedBlob as Blob);
          } catch (error) {
            logger.error(error);
          }
        } else {
          const reader = new FileReader();
          reader.onload = handleFileRead();
          reader.readAsDataURL(file);
          filesToUpload = [];
        }
      }
    },
    deleteImage(index: number) {
      this.images.splice(index, 1);
    },
    openFileInput() {
      const input = this.$refs.imgInput as HTMLInputElement;
      input.click();
    },
    dragover(e: Event) {
      this.isDragging = true;
      e.preventDefault();
    },
    dragleave() {
      this.isDragging = false;
    },
    drop(e: DragEvent) {
      e.preventDefault();
      this.isDragging = false;
      const file = e.dataTransfer?.files[0] as File;
      if (this.checkFileExtension(file)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const target = e.target as FileReader;
          const imageUrl = target.result as string;
          this.saveCustomImage(imageUrl);
        };
        reader.readAsDataURL(file);
      }
    },
    checkFileExtension(file: File) {
      return this.allowedExtensions.some(
        (extension) => file?.name?.toLowerCase().endsWith(extension) || file?.type === "image/heic", // Check for HEIC file type
      );
    },

    saveCustomImage(url: string) {
      this.loading = true;
      const { customer_name, site_id } = this.$route.params;

      return DailyReportRepository.uploadDailyReportCustomImage(
        customer_name as string,
        site_id as string,
        url,
      )
        .then((data) => {
          const singleImg = {
            url: data.url,
            id: data.id,
            source: "custom",
            camera_id: "",
            name: "",
            timestamp: "",
            width: data.width,
            height: data.height,
          } as ReportImageEntry;
          this.images.push(singleImg);
          this.$emit("addCustom", this.images);
        })
        .catch((error) => {
          logger.error(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
});
